import React, { JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { ButtonColor, ButtonSize, ButtonVariant, Button } from '@videoblocks/storywind';

import CallToAction, { CallToActionProps } from '../elements/CallToAction.builder';
import Image from '../elements/ProgressiveImage/ProgressiveImage';
import { TextColor } from '../enums/TextColorEnum';
import getAssetSource from '../utils/getAssetSource';

interface AuxiliaryButtonsProps {
    color: ButtonColor;
    href: string;
    size: ButtonSize;
    variant: ButtonVariant;
    text: string;
}

interface Opacity {
    percentage: number;
}

export interface MediaHeroProps {
    heading: string;
    subheading: string;
    imageSource: string;
    videoSource?: string;
    showCallToAction: boolean;
    callToAction?: CallToActionProps;
    showSecondaryCta: boolean;
    secondaryCta?: CallToActionProps;
    imageSourceAlt?: string;
    verticalAlignment?: 'middle' | 'bottom';
    horizontalAlignment?: 'left' | 'center';
    useOpaqueBackground: boolean;
    textColor: TextColor;
    lazyLoadImage: boolean;
    secondaryButtons: AuxiliaryButtonsProps[];
    opacity?: Opacity;
    tall?: boolean;
    backgroundColor?: string;
}

const AuxiliaryButtons = ({ color, href, variant, text, size }: AuxiliaryButtonsProps) => {
    return (
        <CallToAction
            color={color}
            href={href}
            variant={variant}
            text={text}
            squared={false}
            showCallToAction={true}
            size={size}
        />
    );
};

export default function MediaHero({
    heading,
    subheading,
    imageSource,
    videoSource,
    showCallToAction,
    callToAction,
    showSecondaryCta,
    secondaryCta,
    imageSourceAlt = '',
    horizontalAlignment = 'center',
    verticalAlignment = 'middle',
    useOpaqueBackground,
    textColor = TextColor.white,
    lazyLoadImage,
    secondaryButtons,
    opacity,
    tall,
    backgroundColor,
}: MediaHeroProps): JSX.Element {
    const imageSrc = getAssetSource(imageSource);
    const videoSrc = getAssetSource(videoSource);

    return (
        <header
            className={twMerge(
                horizontalAlignment === 'center' ? 'px-6 md:px-4 justify-center' : 'pl-10',
                verticalAlignment === 'middle' ? 'py-36' : 'md:pt-60 md:pb-16',
                'flex relative w-full heroContainer-textContainer',
            )}
        >
            {videoSource ? (
                <div>
                    <video
                        className="lg:block absolute inset-0 hidden object-cover w-full h-full"
                        autoPlay={true}
                        loop={true}
                        muted={true}
                        playsInline={true}
                        poster={imageSrc}
                        preload="auto"
                    >
                        <source src={videoSrc} type="video/webm" />
                        <source src={videoSrc} type="video/mp4" />
                        <Image src={imageSrc} alt={imageSourceAlt} shouldLazyLoad={lazyLoadImage} fill={true} />
                    </video>
                    <Image
                        className="lg:hidden absolute inset-0 object-cover w-full h-full"
                        src={imageSrc}
                        alt={imageSourceAlt}
                        shouldLazyLoad={lazyLoadImage}
                        fill={true}
                    />
                </div>
            ) : (
                <Image
                    className="absolute inset-0 object-cover w-full h-full"
                    src={imageSrc}
                    alt={imageSourceAlt}
                    shouldLazyLoad={lazyLoadImage}
                    fill={true}
                />
            )}
            {useOpaqueBackground && (
                <div
                    style={{ opacity: opacity?.percentage || 0.45 }}
                    className="absolute inset-0 w-full h-full bg-gray-900"
                />
            )}
            <div
                className={twMerge(
                    verticalAlignment === 'middle' && 'md:py-4 md:px-0 md:pb-6 p-12',
                    horizontalAlignment === 'center' ? 'text-center' : 'flex flex-col md:items-start',
                    backgroundColor && 'rounded-xl md:px-4 py-4 md:py-6',
                    tall && 'md:py-10',
                    'relative block w-full gap-y-6 max-w-3xl',
                    textColor,
                )}
                style={{ backgroundColor }}
            >
                <h1 className="w-full m-0 font-bold text-3xl lg:text-4xl">{heading}</h1>
                {subheading && (
                    <h2 className={twMerge('font-base m-0 lg:text-xl text-base', tall && 'pb-4')}>{subheading}</h2>
                )}
                {secondaryButtons?.length > 0 && (
                    <div
                        className={twMerge(
                            'gap-x-4 gap-y-4 flex md:flex-row flex-col flex-wrap items-center mb-8 justify-center',
                            horizontalAlignment === 'left' && (backgroundColor ? '-ml-20' : '-ml-24'),
                        )}
                    >
                        {secondaryButtons.map((secondaryButtonProps, index) => (
                            <AuxiliaryButtons key={index} {...secondaryButtonProps} />
                        ))}
                    </div>
                )}
                {showCallToAction && (
                    <div className={twMerge('flex', horizontalAlignment === 'center' && 'justify-center text-center')}>
                        <Button className="no-underline" {...callToAction}>
                            {callToAction?.text}
                        </Button>
                        {showSecondaryCta && (
                            <Button className="no-underline ml-2" {...secondaryCta}>
                                {secondaryCta?.text}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}
